import { Box } from "@mui/material";
import { useSelector } from "react-redux";

import {
  Button,
  Divider,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Typography,
} from "@mui/joy";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Vendor_Entity } from "../../entities/vendor";
import {
  formOpenSelector,
  selectedVendorID,
  setFormOpen,
  setSelectedVendorID,
} from "../../redux/filmsReducer";
import { usePositions } from "../../redux/position/positionHooks";
import {
  useCreateVendorMutation,
  useDeleteVendorMutation,
  useUpdateVendorMutation,
} from "../../redux/vendor/vendorEndpoints";
import { useVendor } from "../../redux/vendor/vendorHooks";
import { useAskQuestion } from "../askQuestion/askQuestion";

/**
 *
 * @returns {ReactElement} FormVendor page
 */
export function FormVendor() {
  const vendorID = useSelector(selectedVendorID);
  const { vendor } = useVendor(vendorID);
  const open = useSelector(formOpenSelector("vendor"));
  const [_vendor, setVendor] = useState<Partial<Vendor_Entity>>({});

  const [createVendor, { isLoading: isCreating }] = useCreateVendorMutation();
  const [updateVendor, { isLoading: isUpdating }] = useUpdateVendorMutation();
  const [deleteVendor] = useDeleteVendorMutation();

  const askQuestion = useAskQuestion();

  const dispatch = useDispatch();

  useEffect(() => {
    if (vendor?.id) {
      setVendor(vendor);
    }
  }, [vendor]);

  const onClose = () => {
    dispatch(setFormOpen({ isOpen: false, formID: "vendor" }));
    dispatch(setSelectedVendorID(undefined));
  };

  const save = () => {
    if (vendorID && _vendor) {
      updateVendor({ id: vendorID, body: _vendor });
    } else if (_vendor) {
      createVendor(_vendor)
        .unwrap()
        .then((e) => {
          dispatch(setSelectedVendorID(e.id));
        });
    }
    onClose();
  };

  const askDelete = () => {
    askQuestion("Are you sure?", ["Cancel", "Yes"], {
      subtitle: (
        <Typography>
          You are about to delete a Vendor. Please confirm this action.
        </Typography>
      ),
    }).then((i) => {
      if (i == 1) confirmDelete();
    });
  };

  const confirmDelete = async () => {
    if (vendorID) deleteVendor(vendorID);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog layout="center" sx={{ width: 750, maxWidth: "95vw" }}>
        <ModalClose onClick={onClose} />
        <Typography
          startDecorator={<i className="fa fa-building" />}
          level="h6"
          sx={{ fontWeight: "bold" }}
        >
          {vendorID ? "Edit" : "Create"} Vendor
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {_vendor ? (
          <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}
            >
              <Typography>Information</Typography>
              <Input
                startDecorator="Name:"
                value={_vendor.name ?? ""}
                onChange={(e) =>
                  setVendor((v) => ({ ...v, name: e.target.value }))
                }
                type="text"
                autoFocus
              />
              <Input
                startDecorator="Contact:"
                value={_vendor.contact ?? ""}
                onChange={(e) =>
                  setVendor((v) => ({ ...v, contact: e.target.value }))
                }
                type="text"
              />
              <Input
                startDecorator="Kind:"
                value={_vendor.kind ?? ""}
                onChange={(e) =>
                  setVendor((v) => ({ ...v, kind: e.target.value }))
                }
                type="text"
              />
              <Input
                startDecorator="Email:"
                value={_vendor.email ?? ""}
                onChange={(e) =>
                  setVendor((v) => ({ ...v, email: e.target.value }))
                }
                type="email"
              />
              <Input
                startDecorator="Phone:"
                value={_vendor.phone ?? ""}
                onChange={(e) =>
                  setVendor((v) => ({ ...v, phone: e.target.value }))
                }
                type="text"
                placeholder="(123) 456-7890"
              />
            </Box>
            <Divider orientation="vertical" />
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}
            >
              <Typography>Address</Typography>
              <Input
                startDecorator="Address:"
                value={_vendor.address ?? ""}
                onChange={(e) =>
                  setVendor((v) => ({ ...v, address: e.target.value }))
                }
                type="text"
              />
              <Input
                startDecorator="City:"
                value={_vendor.city ?? ""}
                onChange={(e) =>
                  setVendor((v) => ({ ...v, city: e.target.value }))
                }
                type="text"
              />
              <Input
                startDecorator="Zip Code:"
                value={_vendor.zipcode ?? ""}
                onChange={(e) =>
                  setVendor((v) => ({ ...v, zipcode: e.target.value }))
                }
                type="text"
              />
              <Input
                startDecorator="State:"
                value={_vendor.state ?? ""}
                onChange={(e) =>
                  setVendor((v) => ({ ...v, state: e.target.value }))
                }
                type="text"
              />
            </Box>
          </Box>
        ) : (
          []
        )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {vendorID ? (
          <Button variant="soft" onClick={askDelete} color="danger">
            Delete
          </Button>
        ) : (
          []
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose} color="neutral" variant="soft">
          Cancel
        </Button>
        <Button loading={isCreating || isUpdating} onClick={save}>
          {vendorID ? "Save" : "Create"}
        </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
}

function formatEIN(input: string) {
  try {
    // Remove all non-digit characters
    const digits = input.replace(/\D/g, "");

    // Ensure there are exactly 9 digits
    if (digits.length !== 9) {
      throw new Error("Invalid EIN: must contain exactly 9 digits.");
    }

    // Format as XX-XXXXXXX
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  } catch (error) {
    return "";
  }
}

function formatSSN(input: string) {
  try {
    // Remove all non-digit characters
    const digits = input.replace(/\D/g, "");

    // Ensure there are exactly 9 digits
    if (digits.length !== 9) {
      throw new Error("Invalid SSN: must contain exactly 9 digits.");
    }

    // Format as XXX-XX-XXXX
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  } catch (error) {
    return "";
  }
}
